/**
 * Templates: ready-made jobs Spidra maintains (a small form that runs one of the tools) and their runs.
 *
 *   const { templates, categories, sites } = await fetchTemplates();
 *   const t = await fetchTemplate("google-maps-leads");
 *   const est = await estimateTemplate("google-maps-leads", inputs);      { credits, note, pages } or throws with field errors
 *   const run = await runTemplate("google-maps-leads", inputs);           RunView (status "running" until done)
 *   const run = await fetchTemplateRun(run.runId);                        poll; has rows when done
 */
import api from "@/lib/axios";

export type TemplateInputType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "list"
  | "boolean"
  | "date"
  | "dateRange"
  | "month"
  | "week"
  | "year"
  | "country"
  | "language"
  | "airport";

export interface TemplateInputOption {
  value: string;
  label: string;
  /** Other words the search box matches ("uk", "usa"). */
  keywords?: string;
  /** A storefront's country (amazon.co.uk is GB), for its flag. */
  country?: string;
}

/**
 * When a field, a column or a variant applies, read from another input: equal to `equals` (compared as text), one of
 * `in`, or with neither, set (on, a number other than 0, a non-empty value).
 */
export interface TemplateCondition {
  input: string;
  equals?: string | number | boolean;
  in?: (string | number | boolean)[];
}

/** Field reference: services/templates/types.ts in the backend. */
export interface TemplateInput {
  key: string;
  label: string;
  type: TemplateInputType;
  help?: string;
  placeholder?: string;
  example?: unknown;
  default?: unknown;
  required?: boolean;
  options?: TemplateInputOption[];
  min?: number;
  max?: number;
  maxItems?: number;
  advanced?: boolean;
  multiple?: boolean;
  joinWith?: string;
  /** "today", "today+365", "today-30" or a date. */
  minDate?: string;
  maxDate?: string;
  maxDays?: number;
  notBefore?: string;
  spanDays?: string;
  only?: string[];
  valueCase?: "upper" | "lower";
  subdivisions?: boolean;
  dateRange?: { minDate?: string; maxDate?: string; maxDays?: number };
  itemFormat?: "route";
  /** Shown only when this holds; a hidden field keeps its value in the form but is not sent. */
  showIf?: TemplateCondition;
}

export interface Airport {
  code: string;
  /** "city" is a metro code covering every airport of the city (NYC, LON). */
  kind: "airport" | "city";
  name: string;
  city: string;
  country: string;
  countryName: string;
  region?: string;
  airports?: string[];
}

/** Type-ahead for airport inputs: the 10 best matches by code, city, airport name or country. */
export async function searchAirports(q: string): Promise<Airport[]> {
  const res = await api.get("/templates/lookups/airports", { params: { q } });
  return res.data?.airports ?? [];
}

/** Details of codes already in a form (for their labels). */
export async function lookupAirports(codes: string[]): Promise<Airport[]> {
  if (!codes.length) return [];
  const res = await api.get("/templates/lookups/airports", { params: { codes: codes.join(",") } });
  return res.data?.airports ?? [];
}

export type ColumnFormat = "text" | "number" | "url" | "image" | "date" | "list" | "boolean" | "longtext";

export interface TemplateColumn {
  key: string;
  label: string;
  format?: ColumnFormat;
  /** A default column only when this holds. */
  showIf?: TemplateCondition;
}

export interface TemplateSite {
  key: string;
  name: string;
  domain: string;
}

export type TemplateTool = "batch" | "search" | "map" | "crawl" | "parse";

export interface TemplateSummary {
  slug: string;
  version: number;
  title: string;
  summary: string;
  site: TemplateSite | null;
  categories: string[];
  tool: TemplateTool;
  featured: boolean;
  /** false: the long tail, listed on its site's filter and in search but not in the main grid. */
  gallery?: boolean;
  /** 1 = used by the most customers in the last 30 days; absent until a template has enough use. */
  popularRank?: number;
  status: "active" | "beta" | "hidden";
  fields: string[];
  usesAi: boolean;
  /** Set by an admin: "notice" (usable, with a message) or "off" (paused, runs refused). Absent when plainly on. */
  availability?: { state: "notice" | "off"; message: string | null } | null;
}

export interface TemplateDetail extends TemplateSummary {
  description: string;
  limits: string[];
  inputs: TemplateInput[];
  columns: TemplateColumn[];
  hasRows: boolean;
  sample: Record<string, unknown>[];
  /** Per-option columns and sample: every variant whose `when` holds replaces them, in order. */
  variants?: { when: TemplateCondition; columns?: TemplateColumn[]; sample?: Record<string, unknown>[] }[];
  /** Set by templateFor(): which options gave these columns, so a column choice is remembered per option. */
  variantKey?: string;
}

export interface FieldError {
  field: string;
  message: string;
}

export type RunStatus = "running" | "completed" | "partial" | "failed" | "cancelled";

export interface TemplateRun {
  runId: string;
  template: string;
  templateVersion: number;
  presetId: string | null;
  tool: TemplateTool;
  status: RunStatus;
  inputs: Record<string, unknown>;
  createdAt: string;
  finishedAt: string | null;
  creditsUsed: number | null;
  urlCount: number | null;
  rowCount: number | null;
  progress?: { completed: number; failed: number; total: number };
  rows?: Record<string, unknown>[];
  failures?: { url: string; error: string }[];
  error?: string | null;
  job: { tool: TemplateTool; id: string | null };
  resultsPath?: string;
  /** Rank templates: how many keywords or grid points ranked, and the average position where they did. */
  summary?: { groups: number; ranked: number; notRanked: number; notChecked: number; averagePosition: number | null; bestPosition: number | null };
}

export interface TemplateRunSummary {
  runId: string;
  template: string;
  title: string;
  site: TemplateSite | null;
  presetId: string | null;
  tool: TemplateTool;
  status: RunStatus;
  inputs: Record<string, unknown>;
  creditsUsed: number | null;
  rowCount: number | null;
  urlCount: number | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Thrown when the API rejects the inputs; `fields` maps input key to message. */
export class TemplateInputError extends Error {
  fields: Record<string, string>;
  constructor(message: string, errors: FieldError[]) {
    super(message);
    this.fields = Object.fromEntries(errors.map(e => [e.field, e.message]));
  }
}

function rethrow(err: unknown): never {
  const e = err as { message?: string; response?: { status?: number; data?: { message?: string; errors?: FieldError[] } } };
  const data = e?.response?.data;
  if (e?.response?.status === 422 && Array.isArray(data?.errors)) throw new TemplateInputError(data?.message || "Some inputs need fixing.", data!.errors!);
  throw new Error(data?.message || e?.message || "Something went wrong.");
}

/** What running a preset returns: a template run, or the tool's own response (jobId, batchId, links...). */
export interface PresetRunResponse {
  run?: TemplateRun;
  jobId?: string;
  batchId?: string;
  links?: unknown[];
  presetId?: string;
  [key: string]: unknown;
}

export async function fetchTemplates(): Promise<{ templates: TemplateSummary[]; categories: { key: string; label: string }[]; sites: TemplateSite[] }> {
  const res = await api.get("/templates");
  return { templates: res.data.templates ?? [], categories: res.data.categories ?? [], sites: res.data.sites ?? [] };
}

export async function fetchTemplate(slug: string): Promise<TemplateDetail> {
  const res = await api.get(`/templates/${encodeURIComponent(slug)}`);
  return res.data.template;
}

export async function estimateTemplate(slug: string, inputs: Record<string, unknown>): Promise<{ credits: number | null; note: string | null; pages: number | null }> {
  try {
    const res = await api.post(`/templates/${encodeURIComponent(slug)}/estimate`, { inputs });
    return { credits: res.data.credits, note: res.data.note, pages: res.data.pages };
  } catch (err) {
    rethrow(err);
  }
}

export async function runTemplate(slug: string, inputs: Record<string, unknown>): Promise<TemplateRun> {
  try {
    const res = await api.post(`/templates/${encodeURIComponent(slug)}/runs`, { inputs });
    return res.data.run;
  } catch (err) {
    rethrow(err);
  }
}

export async function fetchTemplateRun(runId: string, rows = true): Promise<TemplateRun> {
  const res = await api.get(`/template-runs/${encodeURIComponent(runId)}`, { params: rows ? {} : { rows: "false" } });
  return res.data.run;
}

export async function fetchTemplateRuns(params: { template?: string; page?: number; limit?: number } = {}): Promise<{ runs: TemplateRunSummary[]; total: number }> {
  const res = await api.get("/template-runs", { params });
  return { runs: res.data.runs ?? [], total: res.data.total ?? 0 };
}

/** Runs a saved preset. Template presets come back as a run; tool presets return that tool's own response. */
export async function runSavedPreset(presetId: string, overrides?: Record<string, unknown>): Promise<PresetRunResponse> {
  try {
    const res = await api.post(`/presets/${encodeURIComponent(presetId)}/runs`, overrides ? { overrides } : {});
    return res.data;
  } catch (err) {
    rethrow(err);
  }
}

/** `columns`: the table columns to show with this preset (the template's own when left out). */
export async function saveTemplatePreset(body: {
  name: string;
  description?: string;
  templateSlug: string;
  inputs: Record<string, unknown>;
  columns?: TemplateColumn[];
}): Promise<string> {
  try {
    const res = await api.post("/presets", { kind: "template", ...body });
    return res.data?.data?.uuid;
  } catch (err) {
    rethrow(err);
  }
}

/** Updates a template preset in place: its name, inputs and columns. */
export async function updateTemplatePreset(id: string, body: { name: string; inputs: Record<string, unknown>; columns?: TemplateColumn[] | null }): Promise<void> {
  try {
    await api.patch(`/presets/${encodeURIComponent(id)}`, body);
  } catch (err) {
    rethrow(err);
  }
}

export async function saveToolPreset(body: { name: string; description?: string; tool: string; config: Record<string, unknown> }): Promise<string> {
  try {
    const res = await api.post("/presets", { kind: "tool", ...body });
    return res.data?.data?.uuid;
  } catch (err) {
    rethrow(err);
  }
}

export async function updatePresetInputs(presetId: string, inputs: Record<string, unknown>): Promise<void> {
  try {
    await api.patch(`/presets/${encodeURIComponent(presetId)}`, { inputs });
  } catch (err) {
    rethrow(err);
  }
}
